/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '../../api/sportsbook-api';
import {
	Subscription,
	SubscriptionFilters,
	StreamMessage,
	isMessageInitialState
} from '../../api/sportsbook-api-types';
import { RootState, ThunkAction } from '../../../../../store/types';
import mergeNormalizedDataWithStreamMessages from '../../utils/merge-normalized-data-with-stream-messages';
import reportSportsbookError from '../../utils/sportsbook-error-reporter';
import selectSportsbookState from '../../sportsbook-state-selector';
import { EventDto, MarketDto, SelectionDto } from '../../api/sbapi/sbapi-api-model';
import { createDeepEqualityCheckSelector } from '../../utils/custom-selector-creator';
import { isEqual } from '@comeon/utility';
import createCachedSelector from 're-reselect';

const isSubscriptionCreated = (metadata?: SubscriptionMetadata) => !!metadata;

const TIMEOUT_BEFORE_CLEAR = 30 * 1000; // 30 seconds
const subscriptionClearedTimeouts: { [ref: string]: NodeJS.Timeout } = {};

type EventData = {
	[ref: string]:
		| {
				events: EventDto[];
				markets: MarketDto[];
				selections: SelectionDto[];
		  }
		| undefined;
};

type SubscriptionMetadata = {
	isLoading?: boolean; // if subscription has no data or error yet. Similar to react-query isPending.
	isFetching?: boolean; // if subscription is fetching data. Similar to react-query isFetching.
	isError?: boolean;
	filters?: SubscriptionFilters;
};

type EventMetadata = {
	[ref: string]: SubscriptionMetadata | undefined;
};

type State = {
	data: EventData;
	metadata: EventMetadata;
};

const initialState: State = {
	data: {},
	metadata: {}
};

const handleSubscriptionStarted = (
	state: State,
	action: PayloadAction<{ ref: string; filters: SubscriptionFilters }>
) => {
	const { ref, filters } = action.payload;
	// if there's no cached data and subscription just started, it's loading
	const isLoading = !state.data[ref];

	state.metadata[ref] = {
		filters,
		isLoading,
		isFetching: true,
		isError: false
	};
};

const handleSubscriptionFailed = (
	state: State,
	action: PayloadAction<{ ref: string; shouldClearEvents?: boolean; error?: Error }>
) => {
	const { ref, shouldClearEvents } = action.payload;

	state.data[ref] = shouldClearEvents ? undefined : state.data[ref];
	state.metadata[ref] = {
		...state.metadata[ref],
		isLoading: false,
		isFetching: false,
		isError: true
	};
};

const handleSubscriptionCleared = (state: State, action: PayloadAction<string>) => {
	const ref = action.payload;

	state.data[ref] = undefined;
	state.metadata[ref] = undefined;
};

const handleUpdatesReceived = (state: State, action: PayloadAction<{ ref: string; messages: StreamMessage[] }>) => {
	const { ref, messages } = action.payload;

	if (!isSubscriptionCreated(state.metadata[ref])) {
		// eslint-disable-next-line no-console
		console.warn(`Received subscription message for non-existing subscription: ${ref}`);
		return;
	}

	const newData = mergeNormalizedDataWithStreamMessages(
		state.data[ref] || { events: [], markets: [], selections: [] },
		messages
	);

	if (!isEqual(state.data[ref], newData)) {
		state.data[ref] = newData;
	}

	state.metadata[ref] = {
		...state.metadata[ref],
		isLoading: false,
		isFetching: false,
		isError: false
	};
};

const eventSubscriptionSlice = createSlice({
	name: 'eventSubscription',
	initialState,
	reducers: {
		subscriptionStarted: handleSubscriptionStarted,
		subscriptionFailed: handleSubscriptionFailed,
		subscriptionClosed: (_state, _action) => {}, // Don't need to do anything here, it's just a signal that subscription was closed.
		subscriptionCleared: handleSubscriptionCleared,
		subscriptionUpdatesReceived: handleUpdatesReceived
	}
});

const {
	subscriptionStarted,
	subscriptionFailed,
	subscriptionClosed,
	subscriptionCleared,
	subscriptionUpdatesReceived
} = eventSubscriptionSlice.actions;

export const selectEventsByRef = createCachedSelector(
	(state: RootState, ref: string) =>
		selectSportsbookState(state).eventSubscription.data[ref] || {
			events: [],
			markets: [],
			selections: []
		},
	(_state: RootState, ref: string) => ref,
	normalizedData => {
		if (!normalizedData) {
			return [];
		}

		const { events, markets, selections } = normalizedData;

		return events.map(event => {
			const eventMarkets = markets
				.filter(m => m.eventId === event.id)
				.map(market => {
					const marketSelections = selections.filter(s => s.marketId === market.id);
					return { ...market, selections: marketSelections };
				});

			return { ...event, markets: eventMarkets };
		});
	}
)(
	(_state: RootState, ref: string) => ref, // Key selector (caches by `ref`)
	{ selectorCreator: createDeepEqualityCheckSelector }
);

export const selectIsErrorByRef = (state: RootState, ref: string): boolean => {
	return selectSportsbookState(state).eventSubscription.metadata[ref]?.isError || false;
};

export const selectIsLoadingByRef = (state: RootState, ref: string): boolean => {
	return selectSportsbookState(state).eventSubscription.metadata[ref]?.isLoading || false;
};

export const selectIsFetchingByRef = (state: RootState, ref: string): boolean => {
	return selectSportsbookState(state).eventSubscription.metadata[ref]?.isFetching || false;
};

export const selectIsInitializedByRef = (state: RootState, ref: string): boolean => {
	const data = selectSportsbookState(state).eventSubscription.data[ref];
	const hasError = selectIsErrorByRef(state, ref);
	return !!data || hasError;
};

/**
 * If subscription is initialized, not loading and has no events, then returns true.
 */
export const selectHasNoEventsByRef = (state: RootState, ref: string): boolean => {
	const isInitialized = selectIsInitializedByRef(state, ref);
	const isLoading = selectIsLoadingByRef(state, ref);
	const hasEvents = selectEventsByRef(state, ref).length > 0;

	return isInitialized && !isLoading && !hasEvents;
};

const closeSubscription =
	(ref: string): ThunkAction =>
	dispatch => {
		Api.unsubscribeFromEvents(ref);
		dispatch(subscriptionClosed(ref));
	};

export const disposeSubscription =
	(ref: string): ThunkAction =>
	dispatch => {
		const hasSubscription = Api.hasSubscription(ref);
		if (!hasSubscription) {
			return;
		}

		dispatch(closeSubscription(ref));

		clearTimeout(subscriptionClearedTimeouts[ref]);
		subscriptionClearedTimeouts[ref] = setTimeout(() => {
			dispatch(subscriptionCleared(ref));
		}, TIMEOUT_BEFORE_CLEAR);
	};

export type SubscribeToEventsParams = Pick<Subscription, 'ref' | 'filters' | 'orderBy' | 'pageSize'>;
export const subscribeToEvents =
	({ ref, filters, orderBy, pageSize }: SubscribeToEventsParams): ThunkAction =>
	dispatch => {
		// Assume data is needed, don't clear it.
		clearTimeout(subscriptionClearedTimeouts[ref]);

		// If subscription already exists, close it first.
		const hasSubscription = Api.hasSubscription(ref);
		if (hasSubscription) {
			dispatch(closeSubscription(ref));
		}

		dispatch(subscriptionStarted({ ref, filters }));
		let hasTriedReconnect = false;

		Api.subscribeToEvents({
			ref,
			filters,
			orderBy,
			pageSize,
			onSubscribed: () => {},
			onMessage: messages => {
				const hasInitialState = !!messages.find(isMessageInitialState);
				if (hasInitialState) {
					hasTriedReconnect = false;
				}
				dispatch(subscriptionUpdatesReceived({ messages, ref }));
			},
			onError: (message, error) => {
				dispatch(subscriptionFailed({ ref }));
				reportSportsbookError(message, error, { subscriptionReference: ref, filters: JSON.stringify(filters) });
			},
			onMaxResubscribeAttemptsReached: (attempts, error) => {
				// After N number of resubscription and reconnection attempts, not possible to connect. Clear to prevent very stale data.
				if (hasTriedReconnect) {
					dispatch(disposeSubscription(ref));
					dispatch(subscriptionFailed({ ref, error }));
					reportSportsbookError(
						`Max resubscribe attempts ${attempts} reached. Failed to subscribe after reconnecting. Subscription disposed.`,
						error,
						{
							subscriptionReference: ref,
							filters: JSON.stringify(filters)
						}
					);
				} else {
					hasTriedReconnect = true;
					void Api.reconnectToSBApiWebsocketClientAndKeepSubscriptions();
				}
			}
		});
	};

export default eventSubscriptionSlice.reducer;
